import React, { FC, Fragment } from 'react';
import { UseQueryResult } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';

import { GetFeedQuery, GetFeedQueryVariables } from 'api';
import { EmptyState } from 'components';
import { generateLink } from 'Links';
import { useFeedDuplicates } from 'hooks';
import { GridSectionTitle, MediumSection, SmallSection } from 'features/feed';

interface Props {
  groupOptions: {
    title: string;
    variables: GetFeedQueryVariables;
    groupQueryResult: UseQueryResult<GetFeedQuery, Error>;
  };
}

const GridTimelineGroup: FC<Props> = ({ groupOptions }) => {
  const { title, variables, groupQueryResult } = groupOptions || {};

  const { t } = useTranslation();
  const { getUniqueResults } = useFeedDuplicates();
  const { data } = groupQueryResult || {};

  let layout = null;
  const results = getUniqueResults(data?.getFeed?.results || []);
  const GroupLinkComponent = generateLink(`/group/${variables?.id}`);

  if (!results.length) {
    return null;
  }

  function getResults(indexes: number[]) {
    return indexes.map(index => results[index]);
  }

  function renderEmptyState() {
    return (
      <EmptyState
        box
        name="groupMember"
        actionComponent={
          <Box paddingTop={2}>
            <Button component={GroupLinkComponent} color="primary" variant="contained" size="large">
              {t('emptyStates:groupMemberAction')}
            </Button>
          </Box>
        }
      />
    );
  }

  switch (results.length) {
    case 0:
      return renderEmptyState();
    case 1:
      layout = <MediumSection results={getResults([0])} />;
      break;
    case 2:
      layout = <MediumSection results={getResults([0, 1])} />;
      break;
    case 3:
    default:
      layout = <SmallSection results={getResults([0, 1, 2])} />;
  }

  return (
    <Fragment>
      <GridSectionTitle component={GroupLinkComponent} title={title} />
      {layout}
    </Fragment>
  );
};

export default GridTimelineGroup;
